import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';

const CATEGORIES = [
  'Traiteur',
  'Lieux d\'évènements', 
  'Personnel évènementiel',
  'Décoration',
  'Mobilier',
  'Animation',
  'Confort',
  'Son et lumière'
];

// Versions courtes pour mobile
const MOBILE_CATEGORIES: Record<string, string> = {
  'Traiteur': 'Traiteur',
  'Lieux d\'évènements': 'Lieux', 
  'Personnel évènementiel': 'Personnel',
  'Décoration': 'Décoration',
  'Mobilier': 'Mobilier',
  'Animation': 'Animation',
  'Confort': 'Confort',
  'Son et lumière': 'Son et lumière'
};

// Mapping entre les catégories d'affichage et celles de la base de données
const CATEGORY_MAPPING: Record<string, string> = {
  'Traiteur': 'traiteur',
  'Lieux d\'évènements': 'lieux-devenements', 
  'Personnel évènementiel': 'personnel-evenementiel',
  'Décoration': 'décoration',
  'Mobilier': 'mobilier',
  'Animation': 'animation',
  'Confort': 'confort',
  'Son et lumière': 'son-et-lumiere'
};

interface CategoriesPickStepProps {
  onComplete: (data: { selectedCategories: string[] }) => void;
  alreadySelected?: string[];
}

export const CategoriesPickStep = ({ onComplete, alreadySelected = [] }: CategoriesPickStepProps) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const toggleCategory = (category: string) => {
    if (alreadySelected.includes(category)) return; // Désactivé si déjà sélectionné
    
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleContinue = () => {
    if (selectedCategories.length > 0) {
      onComplete({ selectedCategories });
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="text-sm text-muted-foreground mb-4">
        Sélectionnez les <strong>catégories</strong> d'offres qui vous intéressent (vous pouvez en choisir plusieurs).
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {CATEGORIES.map((category) => {
          const isSelected = selectedCategories.includes(category);
          const isDisabled = alreadySelected.includes(category);
          
          return (
            <button
              key={category}
              onClick={() => toggleCategory(category)}
              disabled={isDisabled}
              className={`
                p-3 rounded-lg border-2 text-left transition-all duration-200
                ${isSelected 
                  ? 'border-primary bg-primary/10 text-primary' 
                  : isDisabled
                    ? 'border-muted bg-muted/50 text-muted-foreground cursor-not-allowed'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  <span className="hidden sm:inline">{category}</span>
                  <span className="sm:hidden">{MOBILE_CATEGORIES[category]}</span>
                </span>
                {isSelected && <Check className="w-4 h-4" />}
                {isDisabled && (
                  <Badge variant="secondary" className="text-xs">
                    Déjà vu
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selectedCategories.length > 0 && (
        <div className="mt-6 pt-4 border-t">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Sélectionnées :</span>
            {selectedCategories.map((category) => (
              <Badge key={category} variant="secondary">
                {category}
              </Badge>
            ))}
          </div>
          
          <Button 
            onClick={handleContinue}
            className="w-full"
          >
            Continuer ({selectedCategories.length} catégorie{selectedCategories.length > 1 ? 's' : ''})
          </Button>
        </div>
      )}
    </div>
  );
};

// Export du mapping pour utilisation dans d'autres composants
export { CATEGORY_MAPPING };